import { toast } from "sonner";

/**
 * Iframe-safe clipboard copy with graceful fallbacks.
 *
 * 1) Try the modern async Clipboard API (works in standalone tabs / HTTPS).
 * 2) If blocked (iframe permissions policy, insecure context, browser denial),
 *    fall back to the legacy `document.execCommand('copy')` flow via a hidden
 *    textarea. This works in most iframes including the Emergent preview.
 * 3) If even that fails, surface a prompt() so the user can manually Ctrl+C.
 *
 * Returns true if the text most-likely landed on the clipboard, false otherwise.
 * The caller still gets a toast either way so we never silently fail.
 */
export async function copyToClipboard(text, successMessage = "Copied") {
  // Path 1 — async Clipboard API
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      // Must be a secure context (https) AND not blocked by permissions policy.
      window.isSecureContext !== false
    ) {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
      return true;
    }
  } catch (_) {
    // fall through to legacy fallback
  }

  // Path 2 — legacy execCommand via hidden textarea
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    // Avoid scrolling to bottom on iOS
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.setAttribute("readonly", "");
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) {
      toast.success(successMessage);
      return true;
    }
  } catch (_) {
    // fall through to manual prompt
  }

  // Path 3 — last-resort manual copy. window.prompt pre-selects its value on
  // most browsers, so the user just hits Ctrl/Cmd+C.
  try {
    // eslint-disable-next-line no-alert
    window.prompt("Copy with Ctrl+C / Cmd+C, then press Enter:", text);
    toast.message("Manual copy required", {
      description: "Use Ctrl/Cmd+C in the prompt — clipboard access is blocked.",
    });
    return false;
  } catch (_) {
    toast.error("Unable to copy");
    return false;
  }
}
